import {IDocumentAction, isDocumentAction} from "@buildwithflux/core";
import type {AnyAction} from "@buildwithflux/core";
import type {IDocumentData} from "@buildwithflux/models";

import {
    applyActionRecords,
    redoDocument,
    revertActionRecords,
    selectObjects,
    undoDocument,
} from "modules/document/state/actionCreators/documentActions";
import {isBatchAction} from "modules/document/state/helpers/batchActions";

import type {IUndoRedoChange} from "./types";

/**
 * Stack operations interface for undo/redo processing.
 * This is passed to processUndoRedoInStore to allow it to manipulate the store's stacks.
 */
export interface UndoRedoStackOperations {
    pushToUndoStack: (change: IUndoRedoChange) => void;
    pushToRedoStack: (change: IUndoRedoChange) => void;
    clearRedoStack: () => void;
}

/**
 * Helper function to get the undoable action from an action (or batch of actions).
 * Returns the first undoable document action, or undefined if none found.
 *
 * This is used by the Zustand store to determine if an action should be added to the undo stack.
 */
export function getUndoableAction(action: AnyAction): IDocumentAction | undefined {
    if (isBatchAction(action)) {
        // For batch actions, find the first sub-action with canUndo: true
        for (const subAction of action.payload) {
            if (isDocumentAction(subAction) && subAction.payload.canUndo) {
                return subAction;
            }
        }
        return undefined;
    }
    // For non-batch actions, check the action directly
    if (isDocumentAction(action) && action.payload.canUndo === true) {
        return action;
    }
    return undefined;
}

/**
 * Helper function to check if an action is from a remote user (applyActionRecords or revertActionRecords).
 * We don't want to add remote user actions to the undo stack.
 */
export function isRemoteUserAction(action: AnyAction): boolean {
    return action.type === applyActionRecords.type || action.type === revertActionRecords.type;
}

/**
 * Process undo/redo for an action by adding it to the appropriate stack.
 * This is called directly from the Zustand store's reduce method after state is updated.
 *
 * The undo/redo stacks live in the Zustand store state, making them part of the
 * reactive state management system. This function uses the provided stack operations
 * to manipulate the stacks.
 *
 * @param action The action that was just processed (can be batch or non-batch, with or without payload)
 * @param document The document state AFTER the action was applied (contains latestPatch)
 * @param stackOps The stack operations to use for manipulating the undo/redo stacks
 */
export function processUndoRedoInStore(
    action: AnyAction,
    document: IDocumentData | null,
    stackOps?: UndoRedoStackOperations,
): void {
    if (!document?.latestPatch) {
        return;
    }

    // Get the undoable action (or first undoable sub-action in a batch)
    const undoableAction = getUndoableAction(action);

    // Only process if the action is undoable and not from a remote user
    if (undoableAction && !isRemoteUserAction(action)) {
        const patch = document.latestPatch;
        const patches = patch.forward;
        const inversePatches = patch.reverse;

        if (patches.length === 0 && inversePatches.length === 0) return;

        // Update the Zustand store's stacks if stack operations are provided
        if (stackOps) {
            // If we are performing an undo, push the diff to redo stack
            if (undoDocument.match(undoableAction)) {
                stackOps.pushToRedoStack({
                    actionType: undoableAction.type,
                    patchesForRedux: inversePatches,
                });
            } else {
                // If we are performing other undoable actions (including redo!), push the diff to undo stack
                stackOps.pushToUndoStack({
                    actionType: undoableAction.type,
                    patchesForRedux: inversePatches,
                });

                // If we are performing undoable actions OTHER THAN redo or selection, we should clear the redo stack
                // Selection actions should not clear the redo stack to allow users to redo after changing selection
                if (!redoDocument.match(undoableAction) && !selectObjects.match(undoableAction)) {
                    stackOps.clearRedoStack();
                }
            }
        }
    }
}
